import { useState } from "react";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Clock, CheckCircle, Camera, Film, Truck, XCircle, Phone, Mail, Calendar, MapPin
} from "lucide-react";
import { toast } from "sonner";

// Status Colors and Icons
const STATUS_CONFIG = {
  pending: { color: "bg-yellow-500/20 text-yellow-400 border-yellow-500/30", icon: Clock, label: "Pending" },
  confirmed: { color: "bg-blue-500/20 text-blue-400 border-blue-500/30", icon: CheckCircle, label: "Confirmed" },
  shooting: { color: "bg-purple-500/20 text-purple-400 border-purple-500/30", icon: Camera, label: "Shooting" },
  editing: { color: "bg-orange-500/20 text-orange-400 border-orange-500/30", icon: Film, label: "Editing" },
  delivered: { color: "bg-green-500/20 text-green-400 border-green-500/30", icon: Truck, label: "Delivered" },
  cancelled: { color: "bg-red-500/20 text-red-400 border-red-500/30", icon: XCircle, label: "Cancelled" }
};

export default function MyAssignedBookingsTab({ assignedBookings, onRefresh }) {
  const { api } = useAuth();
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [markingHandled, setMarkingHandled] = useState(null);

  const handleMarkHandled = async (bookingId) => {
    try {
      setMarkingHandled(bookingId);
      await api.patch(`/admin/bookings/${bookingId}/mark-handled`);
      toast.success("Booking marked as handled!");
      onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to mark as handled");
    } finally {
      setMarkingHandled(null);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h2 className="font-heading text-2xl text-white uppercase">My Assigned Bookings</h2>
          <p className="text-white/50 font-body text-sm mt-1">
            {assignedBookings.length} booking{assignedBookings.length !== 1 ? 's' : ''} assigned to you
          </p>
        </div>
      </div>

      {/* Bookings List */}
      {assignedBookings.length === 0 ? (
        <div className="bg-[#0a0a0a] border border-white/10 rounded-xl p-12 text-center">
          <Calendar className="mx-auto text-white/30 mb-4" size={48} />
          <p className="text-white/60 font-body">No bookings assigned to you yet.</p>
          <p className="text-white/40 font-body text-sm mt-2">When a Super Admin assigns bookings to you, they will appear here.</p>
        </div>
      ) : (
        <div className="bg-[#0a0a0a] border border-white/10 rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-white/5">
                <tr>
                  <th className="text-left p-4 text-white/60 font-body text-sm uppercase">Customer</th>
                  <th className="text-left p-4 text-white/60 font-body text-sm uppercase">Plan</th>
                  <th className="text-left p-4 text-white/60 font-body text-sm uppercase">Status</th>
                  <th className="text-left p-4 text-white/60 font-body text-sm uppercase">Handled</th>
                  <th className="text-left p-4 text-white/60 font-body text-sm uppercase">Date</th>
                  <th className="text-right p-4 text-white/60 font-body text-sm uppercase">Actions</th>
                </tr>
              </thead>
              <tbody>
                {assignedBookings.map((booking) => {
                  const statusConfig = STATUS_CONFIG[booking.status];
                  const StatusIcon = statusConfig?.icon || Clock;
                  
                  return (
                    <tr 
                      key={booking.id} 
                      className="border-t border-white/5 hover:bg-white/5" 
                      data-testid={`assigned-booking-row-${booking.id}`}
                    >
                      <td className="p-4">
                        <p className="text-white font-body">{booking.name}</p>
                        <p className="text-white/50 text-sm">{booking.phone}</p>
                        {booking.email && <p className="text-white/40 text-xs">{booking.email}</p>}
                      </td>
                      <td className="p-4">
                        <p className="text-white font-body text-sm">{booking.planName}</p>
                        {/* NO PRICE SHOWN - This is intentional for security */}
                      </td>
                      <td className="p-4">
                        <Badge className={`${statusConfig?.color} border`}>
                          <StatusIcon size={12} className="mr-1" />
                          {statusConfig?.label}
                        </Badge>
                      </td>
                      <td className="p-4">
                        {booking.handledByAssignee ? (
                          <Badge className="bg-green-500/20 text-green-400 border-green-500/30 border">
                            <CheckCircle size={12} className="mr-1" />
                            Yes
                          </Badge>
                        ) : (
                          <Badge className="bg-yellow-500/20 text-yellow-400 border-yellow-500/30 border">
                            <Clock size={12} className="mr-1" />
                            Pending
                          </Badge>
                        )}
                      </td>
                      <td className="p-4 text-white/50 text-sm font-body">
                        {new Date(booking.eventDate).toLocaleDateString()}
                      </td>
                      <td className="p-4 text-right">
                        <div className="flex justify-end gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            className="border-white/20 text-white/60 hover:text-white hover:bg-white/10"
                            onClick={() => setSelectedBooking(booking)}
                            data-testid={`view-assigned-booking-${booking.id}`}
                          >
                            View
                          </Button>
                          {!booking.handledByAssignee && (
                            <Button
                              size="sm"
                              className="bg-[#dc2626] hover:bg-[#b91c1c] text-white"
                              onClick={() => handleMarkHandled(booking.id)}
                              disabled={markingHandled === booking.id}
                              data-testid={`mark-handled-${booking.id}`}
                            >
                              {markingHandled === booking.id ? "..." : "Mark Handled"}
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Booking Details Modal */}
      <Dialog open={!!selectedBooking} onOpenChange={() => setSelectedBooking(null)}>
        <DialogContent className="bg-[#0a0a0a] border-white/10 text-white max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl uppercase">Booking Details</DialogTitle>
          </DialogHeader>
          {selectedBooking && (
            <div className="space-y-4">
              {/* Customer Info */}
              <div className="bg-white/5 rounded-lg p-4 space-y-3">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-[#dc2626]/20 flex items-center justify-center">
                    <span className="text-[#dc2626] font-heading text-lg">{selectedBooking.name.charAt(0)}</span>
                  </div>
                  <div>
                    <p className="text-white font-body font-medium">{selectedBooking.name}</p>
                    <div className="flex items-center gap-1 text-white/50 text-sm">
                      <Phone size={12} />
                      <span>{selectedBooking.phone}</span>
                    </div>
                  </div>
                </div>
                {selectedBooking.email && (
                  <div className="flex items-center gap-2 text-white/60 text-sm">
                    <Mail size={14} />
                    <span>{selectedBooking.email}</span>
                  </div>
                )}
                {selectedBooking.city && (
                  <div className="flex items-center gap-2 text-white/60 text-sm">
                    <MapPin size={14} />
                    <span>{selectedBooking.city}</span>
                  </div>
                )}
              </div>

              {/* Booking Info */}
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-white/5 rounded-lg p-3">
                  <p className="text-white/50 text-xs uppercase tracking-wider mb-1">Plan</p>
                  <p className="text-white font-body">{selectedBooking.planName}</p>
                  {/* NO PRICE SHOWN */}
                </div>
                <div className="bg-white/5 rounded-lg p-3">
                  <p className="text-white/50 text-xs uppercase tracking-wider mb-1">Status</p>
                  <Badge className={`${STATUS_CONFIG[selectedBooking.status]?.color} border`}>
                    {STATUS_CONFIG[selectedBooking.status]?.label}
                  </Badge>
                </div>
                {selectedBooking.eventDate && (
                  <div className="bg-white/5 rounded-lg p-3">
                    <p className="text-white/50 text-xs uppercase tracking-wider mb-1">Event Date</p>
                    <p className="text-white font-body">{selectedBooking.eventDate}</p>
                  </div>
                )}
                <div className="bg-white/5 rounded-lg p-3">
                  <p className="text-white/50 text-xs uppercase tracking-wider mb-1">Created</p>
                  <p className="text-white font-body text-sm">{new Date(selectedBooking.createdAt).toLocaleDateString()}</p>
                </div>
              </div>

              {/* Handled Status */}
              <div className="bg-white/5 rounded-lg p-4">
                <p className="text-white/50 text-xs uppercase tracking-wider mb-2">Handled Status</p>
                {selectedBooking.handledByAssignee ? (
                  <div className="flex items-center gap-2 text-green-400">
                    <CheckCircle size={18} />
                    <span className="font-body">Marked as handled on {selectedBooking.handledAt ? new Date(selectedBooking.handledAt).toLocaleDateString() : 'N/A'}</span>
                  </div>
                ) : (
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 text-yellow-400">
                      <Clock size={18} />
                      <span className="font-body">Not yet handled</span>
                    </div>
                    <Button
                      size="sm"
                      className="bg-[#dc2626] hover:bg-[#b91c1c] text-white"
                      onClick={() => {
                        handleMarkHandled(selectedBooking.id);
                        setSelectedBooking(null);
                      }}
                      disabled={markingHandled === selectedBooking.id}
                      data-testid="modal-mark-handled-btn"
                    >
                      Mark Handled
                    </Button>
                  </div>
                )}
              </div>

              <div className="flex justify-end pt-2">
                <Button 
                  variant="outline" 
                  className="border-white/20 text-white/60" 
                  onClick={() => setSelectedBooking(null)}
                >
                  Close
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
